import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import FlipCard from "./FlipCard";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { PrevArrow, NextArrow } from "./ArrowComponents";

import {
  getTopLikedPhotos,
  photoLike,
  getPhotoUrlBatch,
  getBatchPhotoLikeStatus,
} from "../../services/photoService";
import type { Photo } from "../../types/photo";

interface SlideItem {
  photo: Photo;
  src: string;
  liked: boolean;
  likeCount: number;
}

export default function FlipCardSlider() {
  const [items, setItems] = useState<SlideItem[]>([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const width = window.innerWidth;
    let limit = 8;
    if (width < 640) limit = 6;
    else if (width < 1024) limit = 7;

    const fetchPhotos = async () => {
      try {
        const topPhotos: Photo[] = await getTopLikedPhotos(limit);
        const photoIds = topPhotos.map((p) => p.photoId);
        const [urls, statuses] = await Promise.all([
          getPhotoUrlBatch(photoIds),
          getBatchPhotoLikeStatus(photoIds),
        ]);

        const formatted: SlideItem[] = topPhotos.map((p, i) => ({
          photo: p,
          src: urls[p.photoId] ?? "/images/fallback.jpg",
          liked: statuses[i]?.liked ?? false,
          likeCount: statuses[i]?.likeCount ?? 0,
        }));

        setItems(formatted);
      } catch (err) {
        console.error("Failed to fetch top liked photos:", err);
      }
    };

    fetchPhotos();
  }, []);

  const sliderSettings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 500,
    centerMode: true,
    centerPadding: "15%",
    slidesToShow: 2,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 1,
          centerPadding: "20%",
          arrows: true,
          infinite: false,
          prevArrow: <PrevArrow />,
          nextArrow: <NextArrow />,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          centerPadding: "0%",
          arrows: true,
          infinite: false,
          prevArrow: <PrevArrow />,
          nextArrow: <NextArrow />,
        },
      },
    ],
  };

  return (
    <section className="py-9">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center text-gray-500 py-12 space-y-2">
            <span className="block text-3xl font-bold">😔 No Top Liked Photos</span>
            <span className="block">
              No photos have been liked yet. Check back later for trending koalas!
            </span>
          </div>
        ) : (
          <>
            <div>
              <h2 className="flex items-center text-3xl font-bold">Top Liked Photos</h2>
              <p className="text-lg text-gray-600 mb-4">
                Click the photo to view its story.
              </p>
            </div>

            <Slider {...sliderSettings}>
              {items.map(({ photo, src, liked, likeCount }, idx) => (
                <div key={photo.photoId} className="px-4">
                  <FlipCard
                    photo={{ ...photo, src }}
                    liked={liked}
                    likeCount={likeCount}
                    hue={180 + (idx % 6) * 30}
                    onLikeAction={photoLike}
                    onLikeToggle={(photoId, newLiked, newCount) => {
                      setItems((prev) =>
                        prev.map((item) =>
                          item.photo.photoId === photoId
                            ? { ...item, liked: newLiked, likeCount: newCount }
                            : item
                        )
                      );
                    }}
                    onOpenLightbox={(photoId) => {
                      const index = items.findIndex((i) => i.photo.photoId === photoId);
                      if (index >= 0 && index < items.length) {
                        setLightboxIndex(index);
                        setLightboxOpen(true);
                      }
                    }}
                  />
                </div>
              ))}
            </Slider>
          </>
        )}
      </div>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={items.map((i) => ({
          src: i.src,
          alt: i.photo.title || "Untitled",
        }))}
        plugins={[Thumbnails]}
        thumbnails={{
          position: "bottom",
          width: 120,
          height: 80,
          padding: 4,
          gap: 8,
          border: 2,
          borderStyle: "solid",
          borderColor: "rgba(255, 255, 255, 0.3)",
          borderRadius: 4,
        }}
      />
    </section>
  );
}
